import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import {
  accountRequiredMessage,
  type ContractService,
  errors,
  paxRoute,
  type RouteAuth,
  serviceRefusals,
  staffAuth,
  unauthorizedMessage,
} from "../../common";
import {
  appealOpen,
  automodUnavailable,
  moderationRefused,
  surfaceBanned,
} from "../../moderation/v1alpha1/endpoints";
import {
  AddAssetMediaRequest,
  ASSET_MAX_COLLABORATORS,
  AssetCollaboratorParams,
  AssetCollaboratorsResponse,
  AssetCollectionAssetParams,
  AssetCollectionCollaboratorParams,
  AssetCollectionCollaboratorsResponse,
  AssetCollectionIdParams,
  AssetCollectionPage,
  AssetCollectionResponse,
  AssetCopies,
  AssetDraftParams,
  AssetIdParams,
  AssetMediaRejection,
  AssetPage,
  AssetPresets,
  AssetPublishProblems,
  AssetResponse,
  AssetSearchPage,
  AssetSlugParams,
  AssetVersionParams,
  AssetVersionsResponse,
  CreateAssetCollectionRequest,
  CreateAssetRequest,
  CreateAssetUploadRequest,
  CreateAssetUploadResponse,
  ForkAssetCollectionRequest,
  GetAssetQuery,
  ListAssetCollectionsQuery,
  ListAssetsQuery,
  ListAssetTagsQuery,
  ListAssetTagsResponse,
  ListCollectionAssetsQuery,
  ListSimilarAssetsQuery,
  PublishAssetRequest,
  SearchAssetCollectionsQuery,
  SearchAssetsQuery,
  SetAssetArchivedRequest,
  SetAssetReactionRequest,
  SetAssetSignalsRequest,
  SimilarAssets,
  StartAssetDraftRequest,
  UpdateAssetCollectionRequest,
  UpdateAssetMediaRequest,
  UpdateAssetRequest,
} from "./schemas";

export const service = {
  group: "assets",
  version: "v1alpha1",
  stage: "alpha",
  title: "Pax Assets API",
  description:
    "Upload, publish, find, and react to images, video, audio, and basemaps, and curate them into collections.",
} as const satisfies ContractService;

const account: RouteAuth = { kind: "account" };

const accountErrors = {
  UNAUTHORIZED: { message: unauthorizedMessage },
  FORBIDDEN: { message: accountRequiredMessage },
} as const;

const noSuchAsset = {
  NOT_FOUND: {
    message:
      "No asset has that id or version, or it has no published version and the caller can't edit it.",
  },
} as const;

const noSuchCollection = {
  NOT_FOUND: { message: "No collection has that id, or it is someone else's private collection." },
} as const;

const notCollectionEditor = {
  FORBIDDEN: {
    message:
      "Only the collection's owner or a collaborator, or staff for an official collection, can change its assets.",
  },
} as const;

const notCollectionOwner = {
  FORBIDDEN: {
    message: "Only the collection's owner, or staff for an official collection, can change it.",
  },
} as const;

const processingErrors = {
  UNPROCESSABLE_CONTENT: { message: "The file could not be processed.", data: AssetMediaRejection },
  BAD_GATEWAY: {
    message: "Media processing is unavailable right now; start a new upload and try again.",
  },
} as const;

// Assets

const noSuchAssetDraft = {
  NOT_FOUND: { message: "The asset has no draft with that id that the caller can edit." },
  CONFLICT: {
    message:
      "The version is published, publishing, or in appeal, so it can't change; start a draft instead.",
  },
} as const;

const notAssetEditor = {
  FORBIDDEN: { message: "Only the asset's owner and collaborators can work on its drafts." },
} as const;

export const create = oc
  .meta(
    paxRoute({ auth: account, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/assets/v1alpha1",
      operationId: "assets.create",
      summary: "Create an asset.",
      description:
        "Starts a new asset of one kind, owned by the caller, with its first private draft: empty, or with `copyOf` begun from a published version of another asset. Give the draft a file and details, and publish it once `missingForPublish` is empty.",
      inputStructure: "detailed",
      successStatus: 201,
      successDescription: "The new asset at its first draft.",
    }),
  )
  .errors({
    ...surfaceBanned,
    ...errors.badRequest,
    ...accountErrors,
    NOT_FOUND: { message: "`copyOf` names no published version of an asset of that kind." },
  })
  .input(z.object({ body: CreateAssetRequest }))
  .output(AssetResponse);

const noSearchableCollection = {
  NOT_FOUND: {
    message: "A collection in `inAnyCollection` doesn't exist or is someone else's private one.",
  },
} as const;

export const list = oc
  .meta(
    paxRoute({ auth: { kind: "public" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/assets/v1alpha1",
      operationId: "assets.list",
      summary: "List assets.",
      description:
        "Pages through listed assets in the chosen sort, without end, each at its live version. With `ownerId`, pages through one person's assets instead, most recently edited first, and when that person is the caller the assets they collaborate on and their drafts too. Every filter applies at once.",
      inputStructure: "detailed",
      successDescription: "One page of assets.",
    }),
  )
  .errors({
    BAD_REQUEST: {
      message:
        "The filters are invalid, `status` was sent without `ownerId` set to the caller, or the cursor belongs to another listing.",
    },
    ...noSearchableCollection,
  })
  .input(z.object({ query: ListAssetsQuery }))
  .output(AssetPage);

export const search = oc
  .meta(
    paxRoute({
      auth: { kind: "public", actingForGame: "assets:search" },
      minimumTier: null,
      cost: "search",
    }),
    openapi({
      method: "GET",
      path: "/api/assets/v1alpha1/search",
      operationId: "assets.search",
      summary: "Search published assets.",
      description:
        "Ranks listed assets by how closely their live version's title, description, and tags match `q` in meaning, in any language. Every filter applies at once.",
      inputStructure: "detailed",
      successDescription: "One page of the best matches.",
    }),
  )
  .errors({
    BAD_REQUEST: {
      message:
        "The query, filters, or game headers are invalid, or the cursor belongs to another search.",
    },
    ...serviceRefusals,
    NOT_FOUND: {
      message:
        "A collection in `inAnyCollection` doesn't exist or is someone else's private one, or the game named in `x-pax-game-id` doesn't exist.",
    },
    BAD_GATEWAY: { message: "Searching is unavailable right now; try again shortly." },
  })
  .input(z.object({ query: SearchAssetsQuery }))
  .output(AssetSearchPage);

export const get = oc
  .meta(
    paxRoute({
      auth: {
        kind: "public",
        note: "An unpublished asset and a draft are visible only to the asset's owner and collaborators.",
      },
      minimumTier: null,
      cost: "read",
    }),
    openapi({
      method: "GET",
      path: "/api/assets/v1alpha1/{id}",
      operationId: "assets.get",
      summary: "Get an asset.",
      description:
        "Returns an asset at its live version, or at the version `versionId` names. Its owner and collaborators also see it before its first publish, at its newest draft.",
      inputStructure: "detailed",
      successDescription: "The asset.",
    }),
  )
  .errors({ ...errors.badRequest, ...noSuchAsset })
  .input(z.object({ params: AssetIdParams, query: GetAssetQuery }))
  .output(AssetResponse);

export const getBySlug = oc
  .meta(
    paxRoute({
      auth: {
        kind: "public",
        note: "An unpublished asset and a draft are visible only to the asset's owner and collaborators.",
      },
      minimumTier: null,
      cost: "read",
    }),
    openapi({
      method: "GET",
      path: "/api/assets/v1alpha1/by-slug/{kind}/{slug}",
      operationId: "assets.bySlug.get",
      summary: "Get an asset by kind and slug.",
      description:
        "Returns an asset at its live version, or at the version `versionId` names. Its owner and collaborators also see it before its first publish, at its newest draft.",
      inputStructure: "detailed",
      successDescription: "The asset.",
    }),
  )
  .errors({ ...errors.badRequest, ...noSuchAsset })
  .input(z.object({ params: AssetSlugParams, query: GetAssetQuery }))
  .output(AssetResponse);

export const remove = oc
  .meta(
    paxRoute({ auth: account, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "DELETE",
      path: "/api/assets/v1alpha1/{id}",
      operationId: "assets.delete",
      summary: "Delete an asset.",
      description:
        "Deletes an asset that has never been published, with its drafts and collaborators. Only its owner can. The files themselves are kept.",
      inputStructure: "detailed",
      successStatus: 204,
      successDescription: "The asset was deleted.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...accountErrors,
    NOT_FOUND: { message: "The caller can't see an asset with that id." },
    FORBIDDEN: { message: "Only the asset's owner can delete it." },
    CONFLICT: {
      message: "The asset has a published version, so it's never deleted; archive it instead.",
    },
  })
  .input(z.object({ params: AssetIdParams }));

export const setArchived = oc
  .meta(
    paxRoute({ auth: account, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PUT",
      path: "/api/assets/v1alpha1/{id}/archive",
      operationId: "assets.archive.set",
      summary: "Archive an asset, or list it again.",
      description:
        "Takes a published asset out of browsing, searching, and its owner's public shelf, or lists it again. Its versions stay readable, so every world that pinned one keeps showing it. Only its owner can.",
      inputStructure: "detailed",
      successDescription: "The asset at its live version.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...accountErrors,
    NOT_FOUND: { message: "The caller can't see an asset with that id." },
    FORBIDDEN: { message: "Only the asset's owner can archive it." },
    CONFLICT: {
      message:
        "The asset has never been published, so it isn't listed; or a moderator took it down.",
    },
  })
  .input(z.object({ params: AssetIdParams, body: SetAssetArchivedRequest }))
  .output(AssetResponse);

export const listVersions = oc
  .meta(
    paxRoute({ auth: { kind: "public" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/assets/v1alpha1/{id}/versions",
      operationId: "assets.versions.list",
      summary: "List an asset's versions.",
      description:
        "Returns an asset's published versions, newest first, removed ones included. Its owner and collaborators also get its drafts.",
      inputStructure: "detailed",
      successDescription: "The asset's versions.",
    }),
  )
  .errors({ ...errors.badRequest, ...noSuchAsset })
  .input(z.object({ params: AssetIdParams }))
  .output(AssetVersionsResponse);

export const setSignals = oc
  .meta(
    paxRoute({ auth: staffAuth("asset_signals.review"), minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PATCH",
      path: "/api/assets/v1alpha1/{id}/versions/{versionId}/signals",
      operationId: "assets.versions.signals.update",
      summary: "Set a version's signals.",
      description:
        "Sets whether a published version was made with generative AI and whether Pax verified it. When it's the live version, its listing changes with it, so the filters see the change at once.",
      inputStructure: "detailed",
      successDescription: "The asset at that version.",
    }),
  )
  .errors({
    ...errors.badRequest,
    UNAUTHORIZED: { message: unauthorizedMessage },
    FORBIDDEN: { message: "Setting signals needs `asset_signals.review`." },
    NOT_FOUND: { message: "The asset has no version with that id." },
    CONFLICT: {
      message: "The version isn't published, or a moderator took the asset down.",
    },
  })
  .input(z.object({ params: AssetVersionParams, body: SetAssetSignalsRequest }))
  .output(AssetResponse);

export const startDraft = oc
  .meta(
    paxRoute({ auth: account, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/assets/v1alpha1/{id}/drafts",
      operationId: "assets.drafts.create",
      summary: "Start a draft.",
      description:
        "Starts a new draft of the asset, begun from one of its published versions, copying its details and files. The asset's owner and collaborators can.",
      inputStructure: "detailed",
      successStatus: 201,
      successDescription: "The asset at its new draft.",
    }),
  )
  .errors({
    ...surfaceBanned,
    ...errors.badRequest,
    ...accountErrors,
    ...noSuchAsset,
    ...notAssetEditor,
  })
  .input(z.object({ params: AssetIdParams, body: StartAssetDraftRequest }))
  .output(AssetResponse);

export const discardDraft = oc
  .meta(
    paxRoute({ auth: account, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "DELETE",
      path: "/api/assets/v1alpha1/{id}/drafts/{versionId}",
      operationId: "assets.drafts.delete",
      summary: "Discard a draft.",
      description:
        "Deletes a draft that isn't the asset's only version. The asset's owner and collaborators can. Its files are kept.",
      inputStructure: "detailed",
      successStatus: 204,
      successDescription: "The draft was discarded.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...accountErrors,
    NOT_FOUND: noSuchAssetDraft.NOT_FOUND,
    CONFLICT: {
      message:
        "The draft is the asset's only version, or it's publishing or in appeal; delete the asset instead, or wait.",
    },
  })
  .input(z.object({ params: AssetDraftParams }));

export const update = oc
  .meta(
    paxRoute({ auth: account, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PATCH",
      path: "/api/assets/v1alpha1/{id}/drafts/{versionId}",
      operationId: "assets.drafts.update",
      summary: "Edit a draft.",
      description:
        "Changes a draft's title, description, tags, language, or sensitivity. Editing a refused draft returns it to `draft` and gives up its appeal.",
      inputStructure: "detailed",
      successDescription: "The asset at the edited draft.",
    }),
  )
  .errors({
    ...surfaceBanned,
    ...errors.badRequest,
    ...accountErrors,
    ...noSuchAssetDraft,
  })
  .input(z.object({ params: AssetDraftParams, body: UpdateAssetRequest }))
  .output(AssetResponse);

export const createUpload = oc
  .meta(
    paxRoute({ auth: account, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/assets/v1alpha1/{id}/drafts/{versionId}/uploads",
      operationId: "assets.drafts.uploads.create",
      summary: "Start an upload to a draft.",
      description:
        "Returns the upload's id and a presigned URL that accepts a PUT of the file, with exactly the content type and size in the request, for 5 minutes. Then set the upload as the draft's file.",
      inputStructure: "detailed",
      successStatus: 201,
      successDescription: "The upload's id and where to upload the file.",
    }),
  )
  .errors({
    ...surfaceBanned,
    BAD_REQUEST: {
      message:
        "The content type isn't one the draft's kind accepts, the file is over its kind's size limit, or the draft is a basemap's.",
    },
    ...accountErrors,
    ...noSuchAssetDraft,
  })
  .input(z.object({ params: AssetDraftParams, body: CreateAssetUploadRequest }))
  .output(CreateAssetUploadResponse);

export const setMedia = oc
  .meta(
    paxRoute({ auth: account, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PUT",
      path: "/api/assets/v1alpha1/{id}/drafts/{versionId}/media",
      operationId: "assets.drafts.media.set",
      summary: "Set a draft's file.",
      description:
        "Processes an upload to the draft and makes it the draft's file in place of any it had. The earlier file stays in storage. A picture, or a video's still, that the upload gate rates likely sexually explicit is refused with `422`. Automod judges the file at publish. The response waits for processing, which usually takes a few seconds, and up to a couple of minutes for a long video.",
      inputStructure: "detailed",
      successDescription: "The asset at the draft, with its new file.",
    }),
  )
  .errors({
    ...surfaceBanned,
    BAD_REQUEST: { message: "The request is invalid, or the draft is a basemap's." },
    ...accountErrors,
    NOT_FOUND: {
      message:
        "The asset has no draft with that id that the caller can edit, or nothing was uploaded to it under that id.",
    },
    CONFLICT: noSuchAssetDraft.CONFLICT,
    ...processingErrors,
  })
  .input(z.object({ params: AssetDraftParams, body: AddAssetMediaRequest }))
  .output(AssetResponse);

export const updateMedia = oc
  .meta(
    paxRoute({ auth: account, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PATCH",
      path: "/api/assets/v1alpha1/{id}/drafts/{versionId}/media",
      operationId: "assets.drafts.media.update",
      summary: "Process a draft's file again.",
      description:
        "Changes the processing options of a draft's file and makes the files they decide again: an image's icon. The response waits for processing.",
      inputStructure: "detailed",
      successDescription: "The asset at the draft, with its new files.",
    }),
  )
  .errors({
    ...surfaceBanned,
    BAD_REQUEST: {
      message:
        "The options are invalid, aren't for the asset's kind, the kind has nothing to choose, or the draft has no file.",
    },
    ...accountErrors,
    ...noSuchAssetDraft,
    ...processingErrors,
  })
  .input(z.object({ params: AssetDraftParams, body: UpdateAssetMediaRequest }))
  .output(AssetResponse);

export const publish = oc
  .meta(
    paxRoute({ auth: account, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/assets/v1alpha1/{id}/drafts/{versionId}/publish",
      operationId: "assets.drafts.publish",
      summary: "Publish a draft.",
      description:
        "Freezes the draft as `publishing`, has automod check it whole, records the author's copyright confirmation, copies its files to their permanent public URLs, and makes it the asset's live version, the next by number, which never changes again. Publishing gives the version the mandatory tags automod finds. A draft automod refuses becomes `refused` until someone edits it or its uploader appeals. The response waits for all of it.",
      inputStructure: "detailed",
      successDescription: "The asset at its newly published version.",
    }),
  )
  .errors({
    ...surfaceBanned,
    ...moderationRefused,
    ...automodUnavailable,
    ...accountErrors,
    ...noSuchAssetDraft,
    BAD_REQUEST: { message: "A basemap draft is published through the Basemaps API." },
    UNPROCESSABLE_CONTENT: {
      message: "The draft isn't ready to publish.",
      data: AssetPublishProblems,
    },
    BAD_GATEWAY: { message: "Publishing is unavailable right now; try again shortly." },
  })
  .input(z.object({ params: AssetDraftParams, body: PublishAssetRequest }))
  .output(AssetResponse);

export const listCopies = oc
  .meta(
    paxRoute({ auth: account, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/assets/v1alpha1/{id}/drafts/{versionId}/copies",
      operationId: "assets.drafts.copies.list",
      summary: "List a draft's published copies.",
      description:
        "Runs the check publishing runs on a draft as it is now, and returns the published assets that would keep it from counting as unique, so its author sees them before publishing.",
      inputStructure: "detailed",
      successDescription: "The draft's copies.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...accountErrors,
    NOT_FOUND: noSuchAssetDraft.NOT_FOUND,
    BAD_GATEWAY: {
      message: "Comparing a draft's text is unavailable right now; try again shortly.",
    },
  })
  .input(z.object({ params: AssetDraftParams }))
  .output(AssetCopies);

export const listAssetCollaborators = oc
  .meta(
    paxRoute({ auth: account, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/assets/v1alpha1/{id}/collaborators",
      operationId: "assets.collaborators.list",
      summary: "List an asset's collaborators.",
      description:
        "Returns the people who edit the asset's drafts and publish its versions, oldest first. Its owner and collaborators can.",
      inputStructure: "detailed",
      successDescription: "The asset's collaborators.",
    }),
  )
  .errors({ ...errors.badRequest, ...accountErrors, ...noSuchAsset, ...notAssetEditor })
  .input(z.object({ params: AssetIdParams }))
  .output(AssetCollaboratorsResponse);

const notAssetOwner = {
  FORBIDDEN: { message: "Only the asset's owner can manage its collaborators." },
} as const;

export const addAssetCollaborator = oc
  .meta(
    paxRoute({ auth: account, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PUT",
      path: "/api/assets/v1alpha1/{id}/collaborators/{userId}",
      operationId: "assets.collaborators.set",
      summary: "Add a collaborator to an asset.",
      description:
        "Lets a person start and edit the asset's drafts and publish its versions. Adding someone already there answers as if added. Only the owner can.",
      inputStructure: "detailed",
      successDescription: "The asset's collaborators.",
    }),
  )
  .errors({
    ...surfaceBanned,
    ...errors.badRequest,
    ...accountErrors,
    NOT_FOUND: { message: "The caller can't see an asset with that id, or no person has that id." },
    ...notAssetOwner,
    CONFLICT: {
      message: `The asset already has ${ASSET_MAX_COLLABORATORS} collaborators, or that person is its owner.`,
    },
  })
  .input(z.object({ params: AssetCollaboratorParams }))
  .output(AssetCollaboratorsResponse);

export const removeAssetCollaborator = oc
  .meta(
    paxRoute({ auth: account, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "DELETE",
      path: "/api/assets/v1alpha1/{id}/collaborators/{userId}",
      operationId: "assets.collaborators.delete",
      summary: "Remove a collaborator from an asset.",
      description:
        "Takes a person off the asset's collaborators. The drafts they started and the versions they published stay the asset's. Only the owner can.",
      inputStructure: "detailed",
      successDescription: "The asset's collaborators.",
    }),
  )
  .errors({ ...errors.badRequest, ...accountErrors, ...noSuchAsset, ...notAssetOwner })
  .input(z.object({ params: AssetCollaboratorParams }))
  .output(AssetCollaboratorsResponse);

export const setReaction = oc
  .meta(
    paxRoute({ auth: account, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PUT",
      path: "/api/assets/v1alpha1/{id}/reaction",
      operationId: "assets.reaction.set",
      summary: "Like or dislike an asset.",
      description: "Sets the caller's reaction to a published asset, replacing any they had.",
      inputStructure: "detailed",
      successDescription: "The asset with its new counts.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...accountErrors,
    NOT_FOUND: { message: "No published asset has that id." },
  })
  .input(z.object({ params: AssetIdParams, body: SetAssetReactionRequest }))
  .output(AssetResponse);

export const clearReaction = oc
  .meta(
    paxRoute({ auth: account, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "DELETE",
      path: "/api/assets/v1alpha1/{id}/reaction",
      operationId: "assets.reaction.delete",
      summary: "Clear my reaction to an asset.",
      description:
        "Removes the caller's like or dislike. Clearing a reaction they don't have succeeds.",
      inputStructure: "detailed",
      successDescription: "The asset with its new counts.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...accountErrors,
    NOT_FOUND: { message: "No published asset has that id." },
  })
  .input(z.object({ params: AssetIdParams }))
  .output(AssetResponse);

export const listSimilar = oc
  .meta(
    paxRoute({ auth: { kind: "public" }, minimumTier: null, cost: "search" }),
    openapi({
      method: "GET",
      path: "/api/assets/v1alpha1/{id}/similar",
      operationId: "assets.similar.list",
      summary: "List assets like an asset.",
      description:
        "Returns the listed assets of the same kind closest to a published asset's live version in meaning, and the ones whose files are most alike.",
      inputStructure: "detailed",
      successDescription: "The similar assets.",
    }),
  )
  .errors({ ...errors.badRequest, NOT_FOUND: { message: "No published asset has that id." } })
  .input(z.object({ params: AssetIdParams, query: ListSimilarAssetsQuery }))
  .output(SimilarAssets);

export const listPresets = oc
  .meta(
    paxRoute({ auth: { kind: "public" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/assets/v1alpha1/{id}/presets",
      operationId: "assets.presets.list",
      summary: "List the presets that use an asset.",
      description:
        "Returns the published scenarios whose games have used a published asset, the one with the most games that used it first.",
      inputStructure: "detailed",
      successDescription: "The presets that use the asset.",
    }),
  )
  .errors({ ...errors.badRequest, NOT_FOUND: { message: "No published asset has that id." } })
  .input(z.object({ params: AssetIdParams }))
  .output(AssetPresets);

export const listTags = oc
  .meta(
    paxRoute({ auth: { kind: "public" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/assets/v1alpha1/tags",
      operationId: "assets.tags.list",
      summary: "List tags across the library.",
      description:
        "Returns the tags listed assets' live versions carry and how many carry each, most used first. With `prefix`, it suggests tags as a person types one.",
      inputStructure: "detailed",
      successDescription: "Tags with their asset counts.",
    }),
  )
  .errors({ ...errors.badRequest })
  .input(z.object({ query: ListAssetTagsQuery }))
  .output(ListAssetTagsResponse);

// Collections

export const listCollections = oc
  .meta(
    paxRoute({ auth: { kind: "public" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/assets/v1alpha1/collections",
      operationId: "assets.collections.list",
      summary: "List collections.",
      description:
        "Pages through public collections, largest first; staff who curate official collections also see the private official ones. With `ids`, `assetId`, or `editorId`, it narrows to those collections and includes private ones the caller can see, such as their own with `editorId` set to their id.",
      inputStructure: "detailed",
      successDescription: "One page of collections.",
    }),
  )
  .errors({
    ...errors.badRequest,
    NOT_FOUND: { message: "No published asset has that `assetId`." },
  })
  .input(z.object({ query: ListAssetCollectionsQuery }))
  .output(AssetCollectionPage);

export const searchCollections = oc
  .meta(
    paxRoute({ auth: { kind: "public" }, minimumTier: null, cost: "search" }),
    openapi({
      method: "GET",
      path: "/api/assets/v1alpha1/collections/search",
      operationId: "assets.collections.search",
      summary: "Search collections.",
      description:
        "Finds public collections by their titles, the most relevant first, then the larger; staff who curate official collections also find the private official ones. With `editorId`, it searches only the collections that person owns or collaborates on, including private ones the caller can see.",
      inputStructure: "detailed",
      successDescription: "One page of matching collections.",
    }),
  )
  .errors({ ...errors.badRequest })
  .input(z.object({ query: SearchAssetCollectionsQuery }))
  .output(AssetCollectionPage);

export const createCollection = oc
  .meta(
    paxRoute({
      auth: {
        kind: "account",
        note: "An official collection needs `official_collections.manage`.",
      },
      minimumTier: "suspicious",
      cost: "write",
    }),
    openapi({
      method: "POST",
      path: "/api/assets/v1alpha1/collections",
      operationId: "assets.collections.create",
      summary: "Create a collection.",
      description:
        "Creates an empty collection owned by the caller, or an official one with no owner. Automod checks the title and description first; a refused collection is still made, seen only by its owner, and its refusal can be appealed.",
      inputStructure: "detailed",
      successStatus: 201,
      successDescription: "The new collection.",
    }),
  )
  .errors({
    ...surfaceBanned,
    ...moderationRefused,
    ...automodUnavailable,
    ...errors.badRequest,
    UNAUTHORIZED: accountErrors.UNAUTHORIZED,
    FORBIDDEN: {
      message:
        "Connect an account to continue, or hold `official_collections.manage` to create an official collection.",
    },
  })
  .input(z.object({ body: CreateAssetCollectionRequest }))
  .output(AssetCollectionResponse);

export const getCollection = oc
  .meta(
    paxRoute({
      auth: {
        kind: "public",
        note: "A private collection is visible only to its owner and collaborators.",
      },
      minimumTier: null,
      cost: "read",
    }),
    openapi({
      method: "GET",
      path: "/api/assets/v1alpha1/collections/{collectionId}",
      operationId: "assets.collections.get",
      summary: "Get a collection.",
      description:
        "Returns a public collection, or a private one the caller owns or collaborates on.",
      inputStructure: "detailed",
      successDescription: "The collection.",
    }),
  )
  .errors({ ...errors.badRequest, ...noSuchCollection })
  .input(z.object({ params: AssetCollectionIdParams }))
  .output(AssetCollectionResponse);

export const updateCollection = oc
  .meta(
    paxRoute({ auth: account, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PATCH",
      path: "/api/assets/v1alpha1/collections/{collectionId}",
      operationId: "assets.collections.update",
      summary: "Edit a collection.",
      description:
        "Changes a collection's title, description, visibility, or cover. Automod checks a changed title or description together with the other; if it refuses, nothing is saved.",
      inputStructure: "detailed",
      successDescription: "The edited collection.",
    }),
  )
  .errors({
    ...surfaceBanned,
    ...appealOpen,
    ...moderationRefused,
    ...automodUnavailable,
    CONFLICT: { message: "A moderator removed this collection." },
    ...errors.badRequest,
    UNAUTHORIZED: accountErrors.UNAUTHORIZED,
    ...notCollectionOwner,
    ...noSuchCollection,
  })
  .input(z.object({ params: AssetCollectionIdParams, body: UpdateAssetCollectionRequest }))
  .output(AssetCollectionResponse);

export const removeCollection = oc
  .meta(
    paxRoute({ auth: account, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "DELETE",
      path: "/api/assets/v1alpha1/collections/{collectionId}",
      operationId: "assets.collections.delete",
      summary: "Delete a collection.",
      description:
        "Deletes a collection. Its assets stay published, and copies made from it keep theirs.",
      inputStructure: "detailed",
      successStatus: 204,
      successDescription: "The collection was deleted.",
    }),
  )
  .errors({
    ...errors.badRequest,
    UNAUTHORIZED: accountErrors.UNAUTHORIZED,
    ...notCollectionOwner,
    ...noSuchCollection,
  })
  .input(z.object({ params: AssetCollectionIdParams }));

export const listCollectionAssets = oc
  .meta(
    paxRoute({
      auth: {
        kind: "public",
        note: "A private collection is visible only to its owner and collaborators.",
      },
      minimumTier: null,
      cost: "read",
    }),
    openapi({
      method: "GET",
      path: "/api/assets/v1alpha1/collections/{collectionId}/assets",
      operationId: "assets.collections.assets.list",
      summary: "List a collection's assets.",
      description:
        "Pages through a collection's assets, most recently added first. To search inside a collection, use browse or search with `inAnyCollection`.",
      inputStructure: "detailed",
      successDescription: "One page of the collection's assets.",
    }),
  )
  .errors({ ...errors.badRequest, ...noSuchCollection })
  .input(z.object({ params: AssetCollectionIdParams, query: ListCollectionAssetsQuery }))
  .output(AssetPage);

export const addToCollection = oc
  .meta(
    paxRoute({ auth: account, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PUT",
      path: "/api/assets/v1alpha1/collections/{collectionId}/assets/{assetId}",
      operationId: "assets.collections.assets.set",
      summary: "Add an asset to a collection.",
      description:
        "Adds a published asset. Adding one that is already in the collection succeeds and changes nothing.",
      inputStructure: "detailed",
      successDescription: "The collection with its new count.",
    }),
  )
  .errors({
    ...surfaceBanned,
    ...errors.badRequest,
    UNAUTHORIZED: accountErrors.UNAUTHORIZED,
    ...notCollectionEditor,
    NOT_FOUND: { message: "No collection has that id, or no published asset has that id." },
    CONFLICT: { message: "The collection already holds 10,000 assets." },
  })
  .input(z.object({ params: AssetCollectionAssetParams }))
  .output(AssetCollectionResponse);

export const removeFromCollection = oc
  .meta(
    paxRoute({ auth: account, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "DELETE",
      path: "/api/assets/v1alpha1/collections/{collectionId}/assets/{assetId}",
      operationId: "assets.collections.assets.delete",
      summary: "Remove an asset from a collection.",
      description: "Removes an asset. Removing one that isn't in the collection succeeds.",
      inputStructure: "detailed",
      successDescription: "The collection with its new count.",
    }),
  )
  .errors({
    ...errors.badRequest,
    UNAUTHORIZED: accountErrors.UNAUTHORIZED,
    ...notCollectionEditor,
    ...noSuchCollection,
  })
  .input(z.object({ params: AssetCollectionAssetParams }))
  .output(AssetCollectionResponse);

export const forkCollection = oc
  .meta(
    paxRoute({ auth: account, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/assets/v1alpha1/collections/{collectionId}/fork",
      operationId: "assets.collections.fork",
      summary: "Copy a collection.",
      description:
        "Creates a collection owned by the caller holding the same assets. The copy is independent: later changes to either don't reach the other. Automod checks the copy's title and description first; a refused copy is still made, seen only by the caller, and its refusal can be appealed.",
      inputStructure: "detailed",
      successStatus: 201,
      successDescription: "The new copy.",
    }),
  )
  .errors({
    ...surfaceBanned,
    ...moderationRefused,
    ...automodUnavailable,
    ...errors.badRequest,
    ...accountErrors,
    ...noSuchCollection,
  })
  .input(z.object({ params: AssetCollectionIdParams, body: ForkAssetCollectionRequest }))
  .output(AssetCollectionResponse);

// Collaborators

const collaboratorErrors = {
  ...errors.badRequest,
  UNAUTHORIZED: accountErrors.UNAUTHORIZED,
  FORBIDDEN: { message: "Only the collection's owner can manage its collaborators." },
  ...noSuchCollection,
} as const;

export const listCollaborators = oc
  .meta(
    paxRoute({
      auth: {
        kind: "public",
        note: "A private collection's collaborators are visible only to its owner and collaborators.",
      },
      minimumTier: null,
      cost: "read",
    }),
    openapi({
      method: "GET",
      path: "/api/assets/v1alpha1/collections/{collectionId}/collaborators",
      operationId: "assets.collections.collaborators.list",
      summary: "List a collection's collaborators.",
      description: "Returns everyone the owner lets add and remove the collection's assets.",
      inputStructure: "detailed",
      successDescription: "The collection's collaborators.",
    }),
  )
  .errors({ ...errors.badRequest, ...noSuchCollection })
  .input(z.object({ params: AssetCollectionIdParams }))
  .output(AssetCollectionCollaboratorsResponse);

export const addCollaborator = oc
  .meta(
    paxRoute({ auth: account, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PUT",
      path: "/api/assets/v1alpha1/collections/{collectionId}/collaborators/{profileId}",
      operationId: "assets.collections.collaborators.set",
      summary: "Add a collaborator to my collection.",
      description:
        "Lets a person add and remove the collection's assets, and see it while it is private. Adding someone who is already a collaborator succeeds and changes nothing.",
      inputStructure: "detailed",
      successDescription: "The collection's collaborators.",
    }),
  )
  .errors({
    ...surfaceBanned,
    ...collaboratorErrors,
    NOT_FOUND: { message: "No collection has that id, or no profile has that id." },
    CONFLICT: {
      message:
        "An official collection has no collaborators, an owner can't add themselves, a collection automod held back takes none until it's released, and a collection already at its collaborator limit takes no more.",
    },
  })
  .input(z.object({ params: AssetCollectionCollaboratorParams }))
  .output(AssetCollectionCollaboratorsResponse);

export const removeCollaborator = oc
  .meta(
    paxRoute({
      auth: {
        kind: "account",
        note: "The owner can remove anyone, and a collaborator can remove themselves.",
      },
      minimumTier: "suspicious",
      cost: "write",
    }),
    openapi({
      method: "DELETE",
      path: "/api/assets/v1alpha1/collections/{collectionId}/collaborators/{profileId}",
      operationId: "assets.collections.collaborators.delete",
      summary: "Remove a collaborator from a collection.",
      description:
        "Stops a person from changing the collection's assets. Removing someone who isn't a collaborator succeeds.",
      inputStructure: "detailed",
      successDescription: "The collection's collaborators.",
    }),
  )
  .errors({
    ...collaboratorErrors,
    FORBIDDEN: {
      message:
        "Only the collection's owner, or the collaborator themselves, can remove a collaborator.",
    },
  })
  .input(z.object({ params: AssetCollectionCollaboratorParams }))
  .output(AssetCollectionCollaboratorsResponse);
